import {
  Brain, BarChart3, HeartPulse, Layers, ShieldCheck, Users, Clock,
  Award, Building2, Truck, Heart, ShoppingBag, Leaf,
  TrendingUp, Timer, FileCheck, Play,
  Cpu, Database, Factory, Anchor, HandHeart, Package
} from "lucide-react";

export const NAV_LINKS = [
  { label: "Solutions", href: "#pillars" },
  { label: "Industries", href: "#industries" },
  { label: "Results", href: "#proof" },
  { label: "Why Asterisk", href: "#advantage" },
  { label: "Technology", href: "#stack" },
];

export const HERO = {
  headline: "AI-Driven Digital Transformation for SMEs",
  headlineSuffix: "Built for Real Operations",
  subheadline:
    "From ERP and CRM to AI Design and Long-Term Care Billing, we turn complexity into scalable, intelligent systems.",
  primaryCTA: { label: "Book a Strategy Consultation", href: "/consultation" },
  secondaryCTA: { label: "Explore Solutions", href: "#pillars" },
};

export const TRUST_ITEMS = [
  { icon: Clock, value: "38+", label: "Years Established" },
  { icon: ShieldCheck, value: "Microsoft", label: "Cloud Solution Provider" },
  { icon: Users, value: "5,000+", label: "Licensed Users" },
  { icon: HeartPulse, value: "Specialists", label: "in Long-Term Care Systems" },
];

export const PILLARS = [
  {
    id: "erp-foundation",
    icon: Layers,
    tagline: "Pillar 1",
    headline: "Modern ERP for Growing SMEs",
    description:
      "Before businesses can unlock AI, automation, or advanced analytics, they need a clean, connected operational core. Asterisk helps SMEs modernise using Microsoft Dynamics 365 Business Central — one trusted source of truth for finance, operations, and reporting.",
    useCases: [
      "Accounting & financial management",
      "Procurement & vendor management",
      "Sales & customer billing",
      "Inventory & supply chain",
      "Financial reporting & Power BI insights",
    ],
    cta: "Explore SME ERP Solutions",
    href: "/erp-crm",
    color: "#308795",
  },
  {
    id: "ltc-billing",
    icon: HeartPulse,
    tagline: "Pillar 2",
    headline: "The Digital Revenue Backbone for Long-Term Care",
    description:
      "Patient charges, subsidies, insurance claims, and government reporting create a web of manual processes. Asterisk delivers specialised Patient Accounting and Billing built for Long-Term Care — faster billing cycles, improved revenue visibility, and compliance-ready financial management.",
    useCases: [
      "Automated patient billing cycles",
      "Subsidy & grant reconciliation",
      "Government compliance reporting",
      "Integrated financial management",
      "AI-enhanced revenue intelligence",
    ],
    cta: "Explore Patient Billing & Accounting",
    href: "/patient-billing",
    color: "#E95D54",
  },
  {
    id: "ai-design",
    icon: Brain,
    tagline: "Pillar 3",
    headline: "Turning Business Problems Into AI Solutions",
    description:
      "The AI Design Factory is a structured framework that helps SMEs design, prototype, and deploy AI solutions quickly using real business data and practical workflows. Instead of long development cycles, we deliver working AI solutions in weeks.",
    useCases: [
      "Sales forecasting & demand prediction",
      "Customer churn prediction",
      "Inventory optimisation",
      "Financial anomaly detection",
      "Intelligent operational dashboards",
    ],
    cta: "Explore AI Design Factory",
    href: "/data-science",
    color: "#E5BD77",
  },
  {
    id: "data-science",
    icon: BarChart3,
    tagline: "Pillar 4",
    headline: "From Operational Data to Executive Decision Intelligence",
    description:
      "The Fast Track Data Science program helps SMEs rapidly build advanced analytics — turning operational data into real-time intelligence through executive dashboards, predictive analytics, and Digital Twin simulations.",
    useCases: [
      "Executive Control Tower dashboards",
      "Digital Twin for business operations",
      "Predictive revenue forecasting",
      "Scenario simulation & risk modelling",
      "AI-assisted strategic recommendations",
    ],
    cta: "Start Your Data Science Journey",
    href: "/data-science",
    color: "#7EA13E",
  },
];

export const INDUSTRIES = [
  {
    name: "Long-Term Care & Geriatric Care Solutions",
    description: "Nursing Homes & Home Care",
    icon: HeartPulse,
    featured: true,
    href: "/industries/ltc",
  },
  { name: "Retail & Distribution Solutions", description: "Retail Finance and Supply Chain", icon: ShoppingBag, href: "/industries/retail" },
  { name: "Marine & Shipping Solutions", description: "Fleet Finance, Supply Chain & Logistics", icon: Anchor, href: "/industries/marine" },
  { name: "Not-for-Profit Organisations (NPO) & Charities", description: "NPO Finance, Donor and Volunteer Management", icon: HandHeart, href: "/industries/npo" },
  { name: "Food, Beverage & Hospitality Solutions", description: "Central Kitchen, Hospitality Finance and Inventory Management", icon: Heart, href: "/industries/fnb" },
  { name: "Sustainability & ESG", description: "SME ESG Reporting & Sustainable Supply Chain", icon: Leaf, href: "/industries/esg" },
];

export const PROOF_ITEMS = [
  {
    icon: TrendingUp,
    industry: "Healthcare Operator",
    title: "Revenue Optimisation",
    description:
      "Improved billing visibility and reduced reconciliation delays by 35%.",
    metric: "35%",
    metricLabel: "Faster Reconciliation",
  },
  {
    icon: Timer,
    industry: "Distribution Company",
    title: "AI Dashboard Deployment",
    description:
      "Reduced reporting preparation time by 50% using AI dashboards.",
    metric: "50%",
    metricLabel: "Time Saved",
  },
  {
    icon: FileCheck,
    industry: "Not-for-Profit Organisation",
    title: "Automated Compliance",
    description:
      "Automated donor reporting and compliance tracking across operations.",
    metric: "100%",
    metricLabel: "Compliance Coverage",
  },
];

export const ADVANTAGES = [
  { icon: Award, title: "38+ Years of Implementation Experience" },
  { icon: ShieldCheck, title: "Microsoft Cloud Solution Provider" },
  { icon: Users, title: "Average Consultant Experience: 10+ Years" },
  { icon: HeartPulse, title: "Deep Vertical Knowledge in Long-Term Care" },
  { icon: Cpu, title: "AI-Ready Architecture" },
];

export const SME_STACK = {
  layers: [
    {
      level: 3,
      label: "Industry Modules",
      items: ["Patient Billing", "Marine Ops", "NPO", "Distribution"],
      color: "#E95D54",
      icon: Factory,
    },
    {
      level: 2,
      label: "Intelligence",
      items: ["AI Design Factory", "Data Science", "Automation"],
      color: "#E5BD77",
      icon: Brain,
    },
    {
      level: 1,
      label: "Core Systems",
      items: ["ERP", "CRM", "Finance"],
      color: "#308795",
      icon: Database,
    },
  ],
};

export const CTA_OPTIONS = [
  { label: "I Run a Nursing Home", icon: Building2, href: "#contact" },
  { label: "I Want AI for My SME", icon: Brain, href: "#contact" },
  { label: "I Need ERP/CRM", icon: Layers, href: "#contact" },
  { label: "I Want Better Insights", icon: BarChart3, href: "#contact" },
];

export const FOOTER = {
  tagline: "Singapore SME Digital Transformation Partner since 1988.",
  links: [
    {
      heading: "Solutions",
      items: [
        { label: "AI Design Factory", href: "/data-science" },
        { label: "Data Science", href: "/data-science" },
        { label: "Patient Billing & Accounting", href: "/patient-billing" },
        { label: "ERP & CRM", href: "/erp-crm" },
      ],
    },
    {
      heading: "Company",
      items: [
        { label: "About Us", href: "/about" },
        { label: "Careers", href: "/careers" },
        { label: "Partners", href: "/partners" },
        { label: "Privacy Notice", href: "/privacy" },
        { label: "Contact", href: "#contact" },
      ],
    },
    {
      heading: "Resources",
      items: [
        { label: "Blog", href: "/blog" },
        { label: "Case Studies", href: "#proof" },
      ],
    },
  ],
  contact: {
    email: "inquiries@asterisk.com.sg",
    phone: "+65 6744 5527",
    address: "35 Tannery Road #02-07 Tannery Block Ruby Industrial Complex, Singapore 347740",
  },
  copyright: `© ${new Date().getFullYear()} Asterisk Computer Pte Ltd. All rights reserved.`,
};
